<template>
  <div ref="pickerRef" class="calendar-dropdown">
    <!-- Icon mode -->
    <button v-if="displayMode === 'icon'" class="calendar-button" @click="togglePicker">
      <CalendarIcon class="calendar-icon" />
    </button>

    <!-- Input mode -->
    <div v-else class="calendar-input-wrapper" @click="togglePicker">
      <input
        :value="formattedDate"
        :placeholder="placeholder"
        class="calendar-input"
        readonly
        type="text"
      />
    </div>

    <Teleport to="body">
      <div v-if="showPicker" class="calendar-popup" :style="popupStyle" @click.stop>
        <div class="calendar-header">
          <button class="month-nav-button" @click="prevMonth">
            <img class="nav-icon" src="../../assets/icons/chevron-left.svg" />
          </button>
          <span class="month-label">{{ currentMonth.format('MMMM YYYY') }}</span>
          <button class="month-nav-button" @click="nextMonth">
            <img class="nav-icon" src="../../assets/icons/chevron-right.svg" />
          </button>
        </div>

        <div class="weekdays">
          <span v-for="d in daysOfWeek" :key="d">{{ d }}</span>
        </div>

        <div class="days-grid">
          <span
            v-for="day in daysInMonth"
            :key="day.format('YYYY-MM-DD')"
            class="day-cell"
            :class="[
              getDayClass(day),
              isToday(day) ? 'today' : '',
              isOutsideCurrentMonth(day) ? 'outside-month' : '',
            ]"
            @click="selectDate(day)"
          >
            {{ day.date() }}
          </span>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from 'vue';
import moment from 'moment';
import CalendarIcon from './CalendarIcon.vue';
import { useClickOutside } from '@/composables/useClickOutside.js';

const emit = defineEmits(['update:range', 'update:single', 'input', 'closed']);

const props = defineProps({
  range: {
    type: Boolean,
    default: false,
  },
  value: {
    type: [Date, String],
    default: null,
  },
  modelValue: {
    type: [Date, String],
    default: null,
  },
  disabledDates: {
    type: [Array, Object],
    default: () => [],
  },
  format: {
    type: [String, Function],
    default: null,
  },
  highlighted: {
    type: [Date, Array],
    default: null,
  },
  displayMode: {
    type: String,
    default: 'icon', // 'icon' or 'input'
    validator: (value) => ['icon', 'input'].includes(value),
  },
  placeholder: {
    type: String,
    default: 'Select date',
  },
  dateFormat: {
    type: String,
    default: 'YYYY-MM-DD',
  },
});

const showPicker = ref(false);
const pickerRef = ref(null);
const popupStyle = ref({});

const togglePicker = () => {
  showPicker.value = !showPicker.value;
  if (showPicker.value) {
    nextTick(() => {
      updatePopupPosition();
    });
  }
};

const updatePopupPosition = () => {
  if (pickerRef.value) {
    const rect = pickerRef.value.getBoundingClientRect();
    popupStyle.value = {
      position: 'fixed',
      top: `${rect.bottom + 8}px`,
      left: `${rect.left}px`,
      zIndex: 9999,
    };
  }
};

// Update position on scroll and resize
onMounted(() => {
  window.addEventListener('scroll', updatePopupPosition, true);
  window.addEventListener('resize', updatePopupPosition);
});

onUnmounted(() => {
  window.removeEventListener('scroll', updatePopupPosition, true);
  window.removeEventListener('resize', updatePopupPosition);
});

const currentMonth = ref(moment().startOf('month'));

const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const selectedDates = ref([]);

// Initialize selectedDates from value/modelValue prop
const initializeFromProp = () => {
  const initialValue = props.modelValue || props.value;
  if (initialValue) {
    const date = moment(initialValue);
    if (date.isValid()) {
      selectedDates.value = [date];
      currentMonth.value = moment(date).startOf('month');
    }
  }
};

// Initialize on mount
onMounted(() => {
  initializeFromProp();
});

// Watch for prop changes
watch(
  () => props.modelValue || props.value,
  (newVal) => {
    if (newVal) {
      const date = moment(newVal);
      if (date.isValid()) {
        selectedDates.value = [date];
      }
    }
  }
);

const formattedDate = computed(() => {
  if (selectedDates.value.length > 0) {
    return selectedDates.value[0].format(props.dateFormat);
  }
  return '';
});

useClickOutside(pickerRef, () => {
  showPicker.value = false;
});

const daysInMonth = computed(() => {
  const startOfMonth = moment(currentMonth.value).startOf('month');
  const endOfMonth = moment(currentMonth.value).endOf('month');

  const days = [];
  let day = moment(startOfMonth).startOf('week');
  const last = moment(endOfMonth).endOf('week');

  while (day.isBefore(last)) {
    days.push(moment(day));
    day.add(1, 'day');
  }

  return days;
});

function selectDate(date) {
  if (props.range) {
    if (selectedDates.value.length === 2) {
      selectedDates.value = [];
    }
    selectedDates.value.push(moment(date));

    if (selectedDates.value.length === 2) {
      const sorted = [...selectedDates.value].sort((a, b) => a.valueOf() - b.valueOf());
      emit('update:range', [sorted[0], sorted[1]]);
    }
  } else {
    const selected = moment(date);
    selectedDates.value = [selected];
    emit('update:single', selected);
    emit('input', selected);
    showPicker.value = false;
    emit('closed', selected);
  }
}

// Update position when calendar navigation changes
function prevMonth() {
  currentMonth.value = moment(currentMonth.value).subtract(1, 'month');
  nextTick(() => updatePopupPosition());
}

function nextMonth() {
  currentMonth.value = moment(currentMonth.value).add(1, 'month');
  nextTick(() => updatePopupPosition());
}

function getDayClass(date) {
  if (selectedDates.value.length >= 1 && date.isSame(selectedDates.value[0], 'day')) {
    return 'selected';
  }
  if (
    props.range &&
    selectedDates.value.length === 2 &&
    date.isSame(selectedDates.value[1], 'day')
  ) {
    return 'selected';
  }
  return '';
}

function isOutsideCurrentMonth(date) {
  return !date.isSame(currentMonth.value, 'month');
}

function isToday(date) {
  return date.isSame(moment(), 'day');
}
</script>

<style scoped>
.calendar-dropdown {
  position: relative;
  display: inline-block;
  z-index: 999;
}

.calendar-button {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.calendar-button:hover {
  opacity: 0.8;
}

.calendar-icon {
  pointer-events: none;
}

.calendar-input-wrapper {
  width: 100%;
  cursor: pointer;
}

.calendar-input {
  width: 100%;
  height: 53px;
  background-color: #2d2d2d;
  border: none;
  border-radius: 5px;
  color: #fff;
  text-align: center;
  cursor: pointer;
  font-size: 16px;
  padding: 0 16px;
}

.calendar-input::placeholder {
  color: #6c757d;
  opacity: 1;
}

.calendar-input:focus {
  outline: none;
  background-color: #2d2d2d;
}

@media only screen and (max-width: 767px) {
  .calendar-input {
    font-size: 14px;
  }
}

.calendar-popup {
  background-color: #151515;
  color: #fff;
  padding: 16px;
  border-radius: 4px;
  width: 300px;
  border: 1px solid #707070;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}

.calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.month-nav-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.month-nav-button:hover {
  opacity: 0.7;
}

.nav-icon {
  width: 16px;
  height: 16px;
}

.month-label {
  font-weight: bold;
  font-size: 14px;
}

.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 8px;
  gap: 4px;
}

.days-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  font-size: 14px;
  gap: 4px;
}

.day-cell {
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 32px;
  width: 32px;
  margin: 0 auto;
}

.day-cell:hover {
  background-color: #3a3a3a;
}

.day-cell.today {
  border: 0.25px solid #ffc627;
  background-color: rgba(255, 255, 255, 0.08);
}

.day-cell.selected {
  background-color: #ffc627;
  color: #000;
}

.day-cell.outside-month {
  opacity: 0.4;
}
</style>
